/**
 * Écriture des fichiers GPX.
 *
 * Portage de `GpxWriter.kt`. Le fichier produit est un GPX 1.1 valide : les
 * annotations propres à VeloGPX vivent dans un `<extensions>` sous leur propre
 * espace de noms, que les autres applications ignorent sans broncher.
 */

import { encodePolyline6 } from './polyline'
import { escapeXmlAttribute, escapeXmlText } from './xml'
import { VELOGPX_NAMESPACE } from './gpxParser'
import {
  MARKER_TYPE_WAYOUT,
  type CustomMarker,
  type RoutingProfile,
  type TrackPoint,
  type TrackSegment,
  type WayInOut,
  type Waypoint,
} from './types'

/**
 * Taille maximale d'un nom de fichier, en **octets UTF-8**.
 *
 * En octets et non en caractères : la limite des systèmes de fichiers courants
 * est de 255 **octets**. Un plafond de 120 caractères laissait donc passer
 * 360 octets en idéogrammes et 480 en emoji — et le projet promet justement de
 * les préserver. Une trace ainsi nommée voyait *tous* ses exports échouer,
 * sans autre recours que de la renommer.
 *
 * 200 laisse la place au suffixe `.gpx` et au « (1) » que les systèmes
 * ajoutent en cas de collision.
 */
export const MAX_FILENAME_BYTES = 200

export interface GpxWriteOptions {
  readonly creator: string
  /** Écrire les annotations VeloGPX. Désactiver produit un GPX neutre. */
  readonly includeVeloGpxExtensions?: boolean
}

export interface TrackToWrite {
  readonly name: string
  readonly segments: readonly TrackSegment[]
  readonly waypoints: readonly Waypoint[]
  /**
   * Points d'intérêt du fichier que le commutateur d'affichage masque.
   *
   * Écrits quand même : c'est un réglage d'affichage, il n'a pas à décider de
   * ce que le fichier rendu contient. Le rejeu du fichier source n'en a pas
   * l'usage — les `<wpt>` y passent octet pour octet —, ce chemin de repli si.
   */
  readonly hiddenFileWaypoints?: readonly Waypoint[]
  readonly customMarkers: readonly CustomMarker[]
  readonly wayInOuts: readonly WayInOut[]
  readonly wayRoutes: ReadonlyMap<string, readonly TrackPoint[]>
  readonly realStartDistance: number
  readonly kmMarkersFromFictiveStart: boolean
  readonly isRouted: boolean
  readonly routingProfile: RoutingProfile
}

/** Coordonnée : toujours six décimales, soit environ 10 cm. */
function formatCoord(value: number): string {
  return value.toFixed(6)
}

/**
 * Nombre lisible : six décimales, zéros de fin retirés.
 *
 * Produit `120.5` plutôt que `120.500000`, et `0` plutôt que la chaîne vide.
 */
function formatDouble(value: number): string {
  const fixed = value.toFixed(6)
  const trimmed = fixed.includes('.')
    ? fixed.replace(/0+$/, '').replace(/\.$/, '')
    : fixed
  return trimmed === '' || trimmed === '-' ? '0' : trimmed
}

/**
 * Indentation d'un cran, telle que `writeGpx` la pose.
 *
 * Le rejeu du fichier source (`gpxPatch.ts`) n'utilise pas cette valeur : il
 * reprend l'indentation du fichier qu'il patche, dont c'est tout l'intérêt.
 */
const STEP = '  '

/**
 * Sérialise une trace en document GPX 1.1 complet.
 *
 * **Chemin de repli.** L'export passe normalement par `patchGpx`, qui rejoue
 * le fichier source et n'en réécrit que ce qui a changé ; on n'arrive ici que
 * si ce fichier a disparu du dépôt ou n'est pas exploitable. Le document
 * produit ne porte donc que ce que le modèle sait représenter — ni
 * `<metadata>`, ni `<rte>`, ni extensions tierces.
 *
 * Les points d'intérêt sont écrits avant la trace : le schéma GPX impose cet
 * ordre, et un validateur strict rejette l'inverse.
 */
export function writeGpx(
  track: TrackToWrite,
  options: GpxWriteOptions,
): string {
  const includeExtensions = options.includeVeloGpxExtensions ?? true
  const out: string[] = []

  out.push('<?xml version="1.0" encoding="UTF-8"?>')
  out.push(
    `<gpx version="1.1" creator="${escapeXmlAttribute(options.creator)}"` +
      ' xmlns="http://www.topografix.com/GPX/1/1"' +
      (includeExtensions ? ` xmlns:velogpx="${VELOGPX_NAMESPACE}"` : '') +
      '>',
  )

  for (const waypoint of track.waypoints) {
    writeWaypoint(out, waypoint)
  }
  for (const waypoint of track.hiddenFileWaypoints ?? []) {
    writeWaypoint(out, waypoint)
  }

  out.push('  <trk>')
  out.push(`    <name>${escapeXmlText(track.name)}</name>`)

  const body =
    (includeExtensions ? writeExtensionsBlock(track, '    ', STEP) : '') +
    writeSegments(track, '    ', STEP)

  // Les blocs se terminent par un saut de ligne, que la jointure rajouterait.
  if (body.length > 0) out.push(body.slice(0, -1))

  out.push('  </trk>')
  out.push('</gpx>')

  return out.join('\n') + '\n'
}

/**
 * Y a-t-il quelque chose de VeloGPX à écrire sur cette trace ?
 *
 * Décide à la fois de l'ouverture du bloc `<extensions>` et, au rejeu du
 * fichier source, de l'ajout de la déclaration `xmlns:velogpx` — les deux
 * doivent répondre au même critère, sinon un fichier déclare un espace de noms
 * qu'il n'emploie pas, ou emploie un préfixe qu'il ne déclare pas.
 */
export function hasVeloGpxExtensions(track: TrackToWrite): boolean {
  return (
    track.isRouted ||
    track.realStartDistance > 0 ||
    track.kmMarkersFromFictiveStart ||
    exportableMarkers(track).length > 0 ||
    track.wayInOuts.length > 0
  )
}

/**
 * Les marqueurs qui vont dans le fichier.
 *
 * Ceux de way out sont dérivés des way in/out : les écrire créerait des
 * doublons à la relecture, puisqu'ils sont reconstruits à l'import.
 */
function exportableMarkers(track: TrackToWrite): readonly CustomMarker[] {
  return track.customMarkers.filter(
    (marker) => marker.type !== MARKER_TYPE_WAYOUT,
  )
}

/**
 * Les enfants `velogpx:*`, sans leur `<extensions>` englobant.
 *
 * Rendus séparément parce que le rejeu du fichier source les injecte dans le
 * bloc `<extensions>` **du fichier**, à côté des extensions tierces qui y
 * vivent déjà — Garmin, Wikiloc — plutôt que d'en ouvrir un second, que le
 * schéma interdit.
 *
 * L'ordre d'émission est fixe. Le parseur accepte n'importe lequel ; c'est la
 * comparaison de deux fichiers exportés depuis la même trace qui l'exige, et
 * le jeu de référence partagé avec l'APK qui le fixe.
 */
export function writeVeloGpxChildren(
  track: TrackToWrite,
  indent: string,
  step: string,
): string {
  if (!hasVeloGpxExtensions(track)) return ''

  const inner = indent + step
  const out: string[] = []

  // Le profil accompagne toujours l'indicateur, jamais seul : relire « routé »
  // sans savoir avec quel profil ne permettrait pas de recalculer à
  // l'identique, et un profil posé sur une trace d'origine ne voudrait rien
  // dire.
  if (track.isRouted) {
    out.push(`${indent}<velogpx:routed>true</velogpx:routed>`)
    out.push(
      `${indent}<velogpx:routingProfile>${track.routingProfile.id}</velogpx:routingProfile>`,
    )
  }

  if (track.realStartDistance > 0) {
    out.push(
      `${indent}<velogpx:realStartDistance>${formatDouble(track.realStartDistance)}</velogpx:realStartDistance>`,
    )
  }

  if (track.kmMarkersFromFictiveStart) {
    out.push(
      `${indent}<velogpx:kmMarkersFromFictiveStart>true</velogpx:kmMarkersFromFictiveStart>`,
    )
  }

  const markers = exportableMarkers(track)
  if (markers.length > 0) {
    const markerInner = inner + step
    out.push(`${indent}<velogpx:customMarkers>`)
    for (const marker of markers) {
      out.push(
        `${inner}<velogpx:marker type="${escapeXmlAttribute(marker.type)}"` +
          ` symbol="${escapeXmlAttribute(marker.symbol)}"` +
          ` distanceKm="${formatDouble(marker.distanceKm)}"` +
          ` visible="${marker.visible}">`,
      )
      out.push(
        `${markerInner}<velogpx:name>${escapeXmlText(marker.name)}</velogpx:name>`,
      )
      // Émise même vide, comme `GpxWriter.kt`. La relecture donnerait le même
      // marqueur dans les deux cas : c'est la comparaison de deux fichiers
      // exportés depuis la même trace qui exige l'alignement, un écart
      // silencieux sur un détail de sérialisation se cherchant longtemps.
      out.push(
        `${markerInner}<velogpx:description>${escapeXmlText(marker.description)}</velogpx:description>`,
      )
      out.push(`${inner}</velogpx:marker>`)
    }
    out.push(`${indent}</velogpx:customMarkers>`)
  }

  if (track.wayInOuts.length > 0) {
    const wayInner = inner + step
    out.push(`${indent}<velogpx:wayInOuts>`)
    for (const way of track.wayInOuts) {
      out.push(
        `${inner}<velogpx:wayInOut exitKm="${formatDouble(way.exitKm)}"` +
          ` entryKm="${formatDouble(way.entryKm)}"` +
          ` visible="${way.visible}">`,
      )
      out.push(`${wayInner}<velogpx:name>${escapeXmlText(way.name)}</velogpx:name>`)

      for (const via of way.vias) {
        out.push(
          `${wayInner}<velogpx:via lat="${formatCoord(via.lat)}" lon="${formatCoord(via.lon)}"/>`,
        )
      }

      // La géométrie est embarquée pour qu'un réimport n'ait pas besoin du
      // réseau : le jour d'une course, la connexion est rarement disponible.
      const route = track.wayRoutes.get(way.id)
      if (route && route.length >= 2) {
        out.push(
          `${wayInner}<velogpx:route>${escapeXmlText(encodePolyline6(route))}</velogpx:route>`,
        )
      }

      out.push(`${inner}</velogpx:wayInOut>`)
    }
    out.push(`${indent}</velogpx:wayInOuts>`)
  }

  return out.join('\n') + '\n'
}

/** Le bloc `<extensions>` complet, ou la chaîne vide s'il n'a rien à porter. */
export function writeExtensionsBlock(
  track: TrackToWrite,
  indent: string,
  step: string,
): string {
  const children = writeVeloGpxChildren(track, indent + step, step)
  if (children.length === 0) return ''
  return `${indent}<extensions>\n${children}${indent}</extensions>\n`
}

/**
 * Les `<trkseg>` de la trace.
 *
 * Un segment vide est écrit quand même, comme `GpxWriter.kt` : la découpe fait
 * partie de ce que le fichier dit de la trace, et deux exports de la même
 * trace depuis les deux applications doivent rendre la même structure.
 */
export function writeSegments(
  track: TrackToWrite,
  indent: string,
  step: string,
): string {
  const pointIndent = indent + step
  const detailIndent = pointIndent + step
  const out: string[] = []

  for (const segment of track.segments) {
    out.push(`${indent}<trkseg>`)
    for (const point of segment.points) {
      writeTrackPoint(out, point, pointIndent, detailIndent)
    }
    out.push(`${indent}</trkseg>`)
  }

  return out.length === 0 ? '' : out.join('\n') + '\n'
}

function writeWaypoint(out: string[], waypoint: Waypoint): void {
  const attrs = `lat="${formatCoord(waypoint.lat)}" lon="${formatCoord(waypoint.lon)}"`

  const hasName = waypoint.name !== null && waypoint.name.length > 0
  const hasDesc = waypoint.desc !== null && waypoint.desc.length > 0

  if (waypoint.ele === null && !hasName && !hasDesc) {
    out.push(`  <wpt ${attrs}/>`)
    return
  }

  out.push(`  <wpt ${attrs}>`)
  if (waypoint.ele !== null) {
    out.push(`    <ele>${formatDouble(waypoint.ele)}</ele>`)
  }
  if (hasName) {
    out.push(`    <name>${escapeXmlText(waypoint.name!)}</name>`)
  }
  if (hasDesc) {
    out.push(`    <desc>${escapeXmlText(waypoint.desc!)}</desc>`)
  }
  out.push('  </wpt>')
}

function writeTrackPoint(
  out: string[],
  point: TrackPoint,
  indent: string,
  detailIndent: string,
): void {
  const attrs = `lat="${formatCoord(point.lat)}" lon="${formatCoord(point.lon)}"`

  const hasTime = point.time !== null && point.time.length > 0
  const hasName = point.name !== null && point.name.length > 0

  // Forme compacte : sur une trace de 200 000 points, l'économie se compte
  // en mégaoctets.
  if (point.ele === null && !hasTime && !hasName) {
    out.push(`${indent}<trkpt ${attrs}/>`)
    return
  }

  out.push(`${indent}<trkpt ${attrs}>`)
  if (point.ele !== null) {
    out.push(`${detailIndent}<ele>${formatDouble(point.ele)}</ele>`)
  }
  if (hasTime) {
    out.push(`${detailIndent}<time>${escapeXmlText(point.time!)}</time>`)
  }
  if (hasName) {
    out.push(`${detailIndent}<name>${escapeXmlText(point.name!)}</name>`)
  }
  out.push(`${indent}</trkpt>`)
}


/**
 * Rend un nom de trace utilisable comme nom de fichier.
 *
 * La classe de caractères interdits est explicite plutôt que d'utiliser `\w` :
 * ce dernier ne couvre que l'ASCII, et transformerait « Ardèche » en
 * « Ard_che ». Accents, idéogrammes et emoji sont préservés.
 */
export function sanitizeFileName(name: string): string {
  // eslint-disable-next-line no-control-regex
  const unsafe = /[/\\:*?"<>|\x00-\x1F]/g

  const trimEdges = (value: string): string =>
    value.replace(/^[_\s.]+/, '').replace(/[_\s.]+$/, '')

  // Le nettoyage encadre la troncature : couper au milieu peut faire
  // réapparaître un point ou un tiret bas en fin de nom.
  const sanitized = trimEdges(
    truncateToBytes(trimEdges(name.trim().replace(unsafe, '_'))),
  )

  return sanitized.length > 0 ? sanitized : 'track'
}

/**
 * Tronque à `MAX_FILENAME_BYTES` octets UTF-8, sur une frontière de caractère.
 *
 * L'avancée se fait **point de code par point de code**, jamais unité de code
 * par unité de code : couper au milieu d'une paire de substitution produirait
 * un nom porteur d'un demi-caractère, que le système refuse ou affiche en
 * losange. C'est ce que le plafond en caractères faisait déjà, en pire — il
 * comptait des unités UTF-16.
 */
function truncateToBytes(text: string): string {
  let bytes = 0
  let index = 0

  for (const character of text) {
    const code = character.codePointAt(0)!
    const width = code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4
    if (bytes + width > MAX_FILENAME_BYTES) break
    bytes += width
    index += character.length
  }

  return index === text.length ? text : text.slice(0, index)
}
